import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useToolDefinitions } from '@/hooks';
import { useIntegrations } from '@/hooks/use-integrations';
import { getIntegrationName } from '@/modules/integrations/constants';
import type { SettingsColumnHead } from '@/modules/settings';
import type { ToolDefinitionAction } from '@/services';

const SEARCH_DEBOUNCE_MS = 300;

/** The Tool Server log's column titles, rendered in the page's sticky header. */
export const TOOL_SERVER_LOG_COLUMNS: SettingsColumnHead[] = [
	{ id: 'at', header: 'Date & time', className: 'w-[160px]' },
	{ id: 'requestId', header: 'Request ID', className: 'w-[160px]' },
	{ id: 'conversation', header: 'Conversation', className: 'w-[120px]' },
	{ id: 'input', header: 'Input' },
	{ id: 'output', header: 'Output' },
	{ id: 'latency', header: 'Latency', className: 'w-[96px]' },
	{ id: 'statusCode', header: 'Status code', className: 'w-[110px]' }
];

/**
 * The connected integrations, the chosen one's actions, and the choice itself; a missing, unknown or
 * unconnected choice falls back to the first. `connectedIds` are the connected integrations' catalog ids.
 */
export function toolServerSelection(definitions: ToolDefinitionAction[], connectedIds: string[], provider?: string | null, tool?: string | null) {
	const connected = new Set(connectedIds.map((id) => id.toLowerCase()));
	const providers = [...new Set(definitions.map((d) => d.provider))]
		.filter((p) => connected.has(p.toLowerCase()))
		.map((p) => ({ value: p, label: getIntegrationName(p) ?? p.charAt(0).toUpperCase() + p.slice(1) }));
	const chosenProvider = providers.find((p) => p.value.toLowerCase() === provider?.toLowerCase())?.value ?? providers[0]?.value;
	const actions = definitions.filter((d) => d.provider === chosenProvider);
	const chosenTool = actions.find((a) => a.tool === tool)?.tool ?? actions[0]?.tool;
	return { providers, actions, provider: chosenProvider, tool: chosenTool };
}

/**
 * The integration, action, request-ID search and page of the Tool Server log; kept by the Logs page so
 * its header can show them. The integration and action live in `?provider=&tool=`, so an action's page
 * can link straight to its calls.
 */
export function useToolServerLogFilters() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [query, setQuery] = useState('');
	const [requestId, setRequestId] = useState('');
	const [page, setPage] = useState(1);

	useEffect(() => {
		const timer = setTimeout(() => {
			setRequestId(query.trim());
			setPage(1);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [query]);

	const select = (next: { provider: string; tool?: string }) => {
		setSearchParams(
			(current) => {
				const params = new URLSearchParams(current);
				params.set('provider', next.provider);
				// Without a tool, the integration's first action is picked.
				if (next.tool) params.set('tool', next.tool);
				else params.delete('tool');
				return params;
			},
			{ replace: true }
		);
		setPage(1);
	};

	return {
		provider: searchParams.get('provider'),
		tool: searchParams.get('tool'),
		setProvider: (provider: string) => select({ provider }),
		setTool: (provider: string, tool: string) => select({ provider, tool }),
		query,
		setQuery,
		requestId,
		page,
		setPage
	};
}

export type ToolServerLogFilters = ReturnType<typeof useToolServerLogFilters>;

/** What the filters pick, read against the tool definitions and the brand's connected integrations; asked only while the tab is open. */
export function useToolServerSelection(filters: Pick<ToolServerLogFilters, 'provider' | 'tool'>) {
	const definitions = useToolDefinitions();
	const integrations = useIntegrations();
	// The account row's name is the provider's catalog id, as in mergeIntegrationData.
	const connectedIds = useMemo(() => (integrations.data ?? []).filter((i) => i.isConnected).map((i) => i.name), [integrations.data]);
	const selection = useMemo(
		() => toolServerSelection(definitions.data ?? [], connectedIds, filters.provider, filters.tool),
		[definitions.data, connectedIds, filters.provider, filters.tool]
	);
	return {
		...selection,
		hasConnected: connectedIds.length > 0,
		isLoading: definitions.isLoading || integrations.isLoading,
		isError: definitions.isError || integrations.isError,
		refetch: () => Promise.all([definitions.refetch(), integrations.refetch()])
	};
}
